import React, { useState, useEffect } from "react";
import { Breadcrumb } from "components";
import { translate } from "react-switch-lang";
import "survey-core/survey-core.css";
import "./AdminDashboard.scss";
import { useHistory } from "react-router-dom";
import CountdownDuration from "../CountDown/CountdownDuration";
import dayjs from "dayjs";
import FilterDashboard from "./FilterDashboard";
import duration from "dayjs/plugin/duration";
import api from "components/api/api";
import { Spinner } from "reactstrap";
import toast from "react-hot-toast";
import ConfigureDropDown from "pages/configureDropdown/ConfigureDropDown";
dayjs.extend(duration);

const QuizSurvey = ({ t }) => {
  const history = useHistory();
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manageOpen, setManageOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    type: "Survey",
    sort: "",
    date: "",
    participation: "",
  });

  const userRole = localStorage.getItem("userRole") || "ADMIN";
  const userId = localStorage.getItem("userId") || "anonymous";
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementPopup, setShowAnnouncementPopup] = useState(false);
  const [hasShownAnnouncements, setHasShownAnnouncements] = useState(false);

  const fetchAnnouncements = async () => {
    // Check if announcements were already shown in this session
    const sessionKey = `announcements_shown_${userId}`;
    const alreadyShown = sessionStorage.getItem(sessionKey);

    if (alreadyShown) {
      return; // Don't fetch or show again
    }

    try {
      const response = await api.fetchAnnouncements(userId);
      const { success, data } = response.data;

      if (success && data && data.length > 0) {
        setAnnouncements(data);
        setShowAnnouncementPopup(true);
        // Mark as shown for this session
        sessionStorage.setItem(sessionKey, "true");
        setHasShownAnnouncements(true);
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  };

  useEffect(() => {
    fetchSurveys(userId);

    // Fetch announcements for users only (not admin) and only once per session
    if (userRole !== "ADMIN" && !hasShownAnnouncements) {
      fetchAnnouncements();
    }
  }, []);

  const AnnouncementPopup = () => {
    if (!showAnnouncementPopup || announcements.length === 0) return null;

    return (
      <div className="announcement-overlay">
        <div className="announcement-popup">
          <button
            className="close-popup"
            onClick={() => setShowAnnouncementPopup(false)}
          >
            ✕
          </button>
          <h2>📢 New Announcements</h2>
          <div className="announcements-list">
            {announcements.map((announcement) => (
              <div key={announcement.id} className="announcement-item">
                <h3>{announcement.title}</h3>
                <p>{announcement.message}</p>
                <small style={{ color: "#999", fontSize: "12px" }}>
                  {new Date(announcement.createdAt).toLocaleString()}
                </small>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const fetchSurveys = async (userId) => {
    try {
      const isAdmin = userRole === "ADMIN";
      const response = await api.getSurveys(userId, isAdmin);

      const { success, data } = response.data;
      const surveys = data?.content || [];

      if (success && Array.isArray(surveys)) {
        const formatted = surveys.map((item, index) => {
          const questions =
            item.definitionJson?.pages?.reduce((total, page) => {
              return total + (page.elements?.length || 0);
            }, 0) || 0;

          return {
            id: item.id || index,
            title: item.title || "Untitled Survey",
            questions: item.totalQuestion || questions,
            minutes: item.quizTotalDuration
              ? parseInt(item.quizTotalDuration, 10)
              : questions * 1,
            type: item.type,
            isAnnounced: item.isAnnounced,
            status: item.status,
            createdAt: item.createdAt,
            isParticipated: item.isParticipated || false,
            visibilityType: item.visibilityType || "PUBLIC",
            maxRetake: item.maxRetake || 0,
            userDataDisplayFields: item.userDataDisplayFields || [],
            json: {
              title: item.title,
              ...item.definitionJson,
            },
          };
        });

        setSurveys(formatted);

        // Auto-close expired surveys (admin only)
        if (isAdmin) {
          const now = Date.now();

          const expiredIds = surveys
            .filter((item) => {
              const createdAt = new Date(item.createdAt).getTime();
              const duration = parseInt(item.quizTotalDuration, 10);
              return (
                item.status === true &&
                !isNaN(duration) &&
                now > createdAt + duration * 60 * 1000
              );
            })
            .map((item) => item.id);

          if (expiredIds.length > 0) {
            try {
              await Promise.all(expiredIds.map((id) => api.closeSurvey(id)));
              fetchSurveys(userId);
            } catch (err) {
              console.error("Error auto-closing surveys:", err);
            }
          }
        }
      } else {
        console.error("Invalid response format. Loading fallback...");
        loadFallbackSurveys();
      }
    } catch (error) {
      console.error("Fetch error. Loading fallback data...", error);
      loadFallbackSurveys();
    } finally {
      setLoading(false);
    }
  };

  const loadFallbackSurveys = () => {
    setSurveys([
      {
        id: "687755d348314e772bbcec0a",
        type: "Quiz",
        title: "new quiz",
        questions: 6,
        status: true,
        minutes: 141,
        isAnnounced: true,
        createdAt: "2025-07-21T10:08:43.743Z",
        visibilityType: "PUBLIC",
      },
      {
        id: "687792b6546e7a01b0e7f176",
        type: "Survey",
        title: "AI Survey",
        questions: 0,
        status: false,
        minutes: 0,
        isAnnounced: true,
        createdAt: "2025-07-16T11:53:26.782Z",
        visibilityType: "PRIVATE",
      },
    ]);
  };

  useEffect(() => {
    fetchSurveys(userId);
  }, []);

  const takeSurvey = async (surveyId) => {
    history.push(`/take/${surveyId}`);
  };

  const handleReopen = async (id) => {
    try {
      // Find the survey to get its original createdAt and duration
      const survey = surveys.find((s) => s.id === id);

      if (!survey) {
        toast.error("Survey not found!");
        return;
      }

      const originalCreatedAt = new Date(survey.createdAt).getTime();
      const currentTime = Date.now();
      const originalDuration = survey.minutes; // in minutes

      // Calculate elapsed time since creation (in minutes)
      const elapsedMinutes = Math.ceil(
        (currentTime - originalCreatedAt) / (60 * 1000)
      );

      // New total duration = original + elapsed + 10 minutes extension
      const newTotalDuration = originalDuration + elapsedMinutes + 10;

      await api.reopenSurvey(id, newTotalDuration);

      // Refetch to get updated data from backend
      await fetchSurveys(userId);

      toast.success(`Survey reopened`);
    } catch (error) {
      console.error("Error reopening survey:", error);
      toast.error("Failed to reopen survey.");
    }
  };

  const closeSurvey = async (id) => {
    try {
      await api.closeSurvey(id);

      setSurveys((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status: false } : s))
      );

      toast.success("Survey or Quiz closed successfully!");
    } catch (error) {
      console.error("Error closing survey:", error);
      toast.error("Failed to close survey.");
    }
  };

  const deleteSurvey = async (id) => {
    try {
      api.deleteSurvey(id);
      setSurveys((prev) => prev.filter((s) => s.id !== id));
      toast.success("Survey deleted successfully!");
    } catch (error) {
      console.error("Error deleting survey:", error);
      toast.error("Failed to delete survey.");
    }
  };

  const handleAnnounceResult = async (id) => {
    const payload = {
      quizSurveyId: id,
      message: "Result announced",
    };

    try {
      const response = await api.announceResult(payload);

      if (response.data.success) {
        fetchSurveys(userId);
        toast.success("Result announced.");
      } else {
        toast.error("Announcement failed. Server did not confirm success.");
      }
    } catch (error) {
      console.error("Error announcing result:", error);
      toast.error("Failed to announce result.");
    }
  };

  const handleSeeResult = (id, type, title) => {
    history.push(`/result/${id}/${type.toLowerCase()}`);
    localStorage.setItem("Title", title);
  };
  const handleSeeInsight = (id, type) => {
    history.push(`/insight/${id}/${type.toLowerCase()}`);
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner
          animation="border"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        />
      </div>
    );
  }

  return (
    <>
      <Breadcrumb title={t("Quiz & Survey")} />
      <div className="app-wrapper" style={{}}>
        <div className="animated slideInUpTiny animation-duration-3">
          <div className="row dashboardDetails">
            <div className={"col-md-12 col-sm-12 col-xs-12"}>
              <div className="admin-dashboard">
                <section>
                  <FilterDashboard
                    filters={filters}
                    setFilters={setFilters}
                    isAdmin={userRole === "ADMIN"}
                  />

                  <h2>
                    {filters.type === "Survey"
                      ? filters.participation === "participated"
                        ? "Participated Surveys"
                        : filters.participation === "notParticipated"
                        ? "Not Participated Surveys"
                        : "Active Surveys"
                      : filters.type === "Quiz"
                      ? filters.participation === "participated"
                        ? "Participated Quizzes"
                        : filters.participation === "notParticipated"
                        ? "Not Participated Quizzes"
                        : "Active Quizzes"
                      : filters.participation === "participated"
                      ? "Participated Surveys & Quizzes"
                      : filters.participation === "notParticipated"
                      ? "Not Participated Surveys & Quizzes"
                      : "Active Surveys & Quizzes"}
                  </h2>

                  <div className="card-grid">
                    {surveys
                      .filter((s) => {
                        if (
                          filters.search &&
                          !s.title
                            .toLowerCase()
                            .includes(filters.search.toLowerCase())
                        )
                          return false;

                        if (filters.type && s.type !== filters.type)
                          return false;

                        if (filters.status === "active" && !s.status)
                          return false;
                        if (filters.status === "closed" && s.status)
                          return false;
                        if (filters.status === "expired") {
                          const endTime =
                            new Date(s.createdAt).getTime() +
                            s.minutes * 60 * 1000;
                          if (Date.now() <= endTime) return false;
                        }

                        if (
                          filters.date &&
                          new Date(s.createdAt).toISOString().slice(0, 10) !==
                            filters.date
                        )
                          return false;

                        if (
                          filters.participation === "participated" &&
                          !s.isParticipated
                        )
                          return false;
                        if (
                          filters.participation === "notParticipated" &&
                          s.isParticipated
                        )
                          return false;

                        return s.status === true;
                      })
                      .sort((a, b) => {
                        if (filters.sort === "latest") {
                          return new Date(b.createdAt) - new Date(a.createdAt);
                        } else if (filters.sort === "oldest") {
                          return new Date(a.createdAt) - new Date(b.createdAt);
                        }
                        return 0;
                      })
                      .map((s) => (
                        <div className="survey-card" key={s.id}>
                          {s.visibilityType === "PRIVATE" && (
                            <span className="lock-icon" title="Private">
                              <span className="material-icons">lock</span>
                            </span>
                          )}
                          <h3>{s.title}</h3>
                          <p>
                            {s.questions} questions (
                            <CountdownDuration
                              createdAt={s.createdAt}
                              minutes={s.minutes}
                            />
                            )
                          </p>

                          {userRole === "ADMIN" ? (
                            <button
                              className="btn close-btn"
                              onClick={() => closeSurvey(s.id)}
                            >
                              Close {s.type}
                            </button>
                          ) : !s.isParticipated ? (
                            <button
                              className="btn take"
                              onClick={() => takeSurvey(s.id)}
                            >
                              Participate
                            </button>
                          ) : s.type === "Survey" ? (
                            <span className="already-participated">
                              Already Participated
                            </span>
                          ) : s.maxRetake <= 0 ? (
                            <span className="already-participated">
                              Already Participated
                            </span>
                          ) : (
                            <button
                              className="btn take"
                              onClick={() => takeSurvey(s.id)}
                            >
                              Re-attempt ({s.maxRetake} left)
                            </button>
                          )}
                        </div>
                      ))}
                  </div>
                </section>

                <section>
                  <h2>
                    {filters.type === "Survey"
                      ? filters.participation === "participated"
                        ? "Closed & Participated Surveys"
                        : filters.participation === "notParticipated"
                        ? "Closed & Not Participated Surveys"
                        : "Closed Surveys"
                      : filters.type === "Quiz"
                      ? filters.participation === "participated"
                        ? "Closed & Participated Quizzes"
                        : filters.participation === "notParticipated"
                        ? "Closed & Not Participated Quizzes"
                        : "Closed Quizzes"
                      : filters.participation === "participated"
                      ? "Closed & Participated Surveys & Quizzes"
                      : filters.participation === "notParticipated"
                      ? "Closed & Not Participated Surveys & Quizzes"
                      : "Closed Surveys & Quizzes"}
                  </h2>

                  <div className="card-grid">
                    {surveys
                      .filter((s) => {
                        if (
                          filters.search &&
                          !s.title
                            .toLowerCase()
                            .includes(filters.search.toLowerCase())
                        )
                          return false;

                        if (filters.type && s.type !== filters.type)
                          return false;

                        if (filters.status === "active" && s.status === false)
                          return false;
                        if (filters.status === "closed" && s.status === true)
                          return false;

                        if (filters.status === "expired") {
                          const endTime =
                            new Date(s.createdAt).getTime() +
                            s.minutes * 60 * 1000;
                          if (Date.now() <= endTime) return false;
                        }

                        if (
                          filters.date &&
                          new Date(s.createdAt).toISOString().slice(0, 10) !==
                            filters.date
                        )
                          return false;

                        if (
                          filters.participation === "participated" &&
                          !s.isParticipated
                        )
                          return false;
                        if (
                          filters.participation === "notParticipated" &&
                          s.isParticipated
                        )
                          return false;

                        return s.status === false;
                      })
                      .sort((a, b) => {
                        if (filters.sort === "latest") {
                          return new Date(b.createdAt) - new Date(a.createdAt);
                        } else if (filters.sort === "oldest") {
                          return new Date(a.createdAt) - new Date(b.createdAt);
                        }
                        return 0;
                      })
                      .map((s) => (
                        <div className="survey-card" key={s.id}>
                          {s.visibilityType === "PRIVATE" && (
                            <span className="lock-icon" title="Private">
                              <span className="material-icons">lock</span>
                            </span>
                          )}
                          <h3>{s.title}</h3>
                          <p>
                            {s.questions} questions (
                            <CountdownDuration
                              createdAt={s.createdAt}
                              minutes={s.minutes}
                            />
                            )
                          </p>

                          {userRole === "ADMIN" ? (
                            <>
                              <button
                                className="btn result"
                                onClick={() =>
                                  handleSeeResult(s.id, s.type, s.title)
                                }
                              >
                                See Results
                              </button>
                              <button
                                className="btn result"
                                onClick={() => handleSeeInsight(s.id, s.type)}
                              >
                                Performance Insight
                              </button>
                              <button
                                className="btn result"
                                onClick={() => {
                                  setSelectedSurvey(s);
                                  setManageOpen(true);
                                }}
                              >
                                Manage
                              </button>
                              {selectedSurvey && selectedSurvey.id === s.id && (
                                <ConfigureDropDown
                                  isOpen={manageOpen}
                                  surveyId={s.id}
                                  userDataDisplayFields={
                                    s.userDataDisplayFields || []
                                  }
                                  toggle={() => {
                                    setManageOpen(!manageOpen);
                                    setSelectedSurvey(null);
                                  }}
                                  onConfigSave={() => {
                                    fetchSurveys(userId);
                                  }}
                                />
                              )}
                            </>
                          ) : !s.isParticipated ? (
                            <p style={{ color: "crimson", fontWeight: 600 }}>
                              ❌ You didn't participate
                            </p>
                          ) : s.visibilityType === "PRIVATE" ? (
                            <p style={{ color: "#ff9800", fontWeight: 600 }}>
                              🔒 This {s.type.toLowerCase()} is private
                            </p>
                          ) : s.isAnnounced ? (
                            <button
                              className="btn result"
                              onClick={() =>
                                handleSeeResult(s.id, s.type, s.title)
                              }
                            >
                              See Results
                            </button>
                          ) : (
                            <p style={{ color: "#888", fontWeight: 500 }}>
                              Not Announced Yet
                            </p>
                          )}

                          {userRole === "ADMIN" && (
                            <>
                              <button
                                className="btn result"
                                onClick={() => handleAnnounceResult(s.id)}
                                disabled={s.isAnnounced === true}
                              >
                                {s.isAnnounced === true
                                  ? "✅ Already Announced"
                                  : "Announce Result"}
                              </button>

                              <button
                                className="btn result"
                                onClick={() => handleReopen(s.id)}
                              >
                                Re-Open {s.title}
                              </button>

                              <button
                                className="btn close-btn"
                                onClick={() => deleteSurvey(s.id)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AnnouncementPopup />
    </>
  );
};

export default translate(QuizSurvey);
